import React, { useRef, useState, useCallback } from 'react';
import * as yup from 'yup';
import { observer } from 'mobx-react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import { Box, Button, Typography } from '@mui/material';
import { useDataProvider, useLocalization } from '@ui-framework/index';
import { useNavigate } from 'react-router-dom';
import { FormattedMessage } from 'react-intl';
import { DialogCloseButton } from '@product-framework/components/Dialogs';
import {
  IconLibrary,
  useAvailabilitySlotTypes,
  useServiceJob,
} from '@product-framework/index';
import Form from '@product-framework/components/AdvancedForms/Form';
import dayjs from 'dayjs';

const FORM_ID = 'DISPATCH_ALTERNATE_TIME_FORM';

const schema = [
  {
    control: 'custom',
    customComponent: 'CustomerServiceRequest',
    dateComponentTitle: {
      localeId: 'APPOINTMENT_DATE',
      defaultMessage: 'Appointment Date',
    },
    name: 'customerServiceRequest',
    validationMessage: {
      id: 'DATE_IS_REQUIRED',
      defaultMessage: 'Date is required',
    },
    column: 1,
    row: 1,
    required: false,
    max: 3,
  },
];

function validationSchema(formatMessageFn) {
  const dateRequired = formatMessageFn(schema[0].validationMessage);
  // Use mixed() so the path matches the schema field name; nested object fields are omitted.
  return yup.object().shape({
    customerServiceRequest: yup
      .mixed()
      .test(
        'request-date-required',
        dateRequired,
        function requestDateRequired(value) {
          if (!value?.requestDate) {
            return this.createError({
              path: `${this.path}.requestDate`,
              message: dateRequired,
            });
          }
          if (dayjs(value?.requestDate).isBefore(dayjs(), 'day')) {
            return this.createError({
              path: `${this.path}.requestDate`,
              message: formatMessageFn({
                id: 'DATE_CANNOT_BE_IN_THE_PAST',
                defaultMessage: 'Date cannot be in the past',
              }),
            });
          }
          return true;
        },
      ),
  });
}

function ServiceDateForm({
  preferredServiceProviderId = null,
  isRescheduleFlow = false,
  rescheduleBySP = false,
}) {
  const formRef = useRef();
  const navigate = useNavigate();
  const { serviceJob } = useServiceJob();
  const { formatMessage } = useLocalization();
  const { slotTypes } = useAvailabilitySlotTypes();

  const jobId = serviceJob?.id;
  const jobNumber = serviceJob?.serviceJobNumber;
  const recordVersion = serviceJob?.recordVersion;

  const [success, setSuccess] = useState(false);
  const [open, setOpen] = useState(false);

  const provider =
    process.env.NODE_ENV === 'development' ? 'sxclServices' : 'default';
  const { put, post, getApiUrl } = useDataProvider(provider);

  const updateJob = async data => {
    const url = `${getApiUrl()}/sxcl/v1/service-jobs/${jobId}`;
    const response = await put(url, {
      appointmentDate: data?.customerServiceRequest?.requestDate,
      slotTypeCode: data?.customerServiceRequest?.slotTypeCode,
      recordVersion,
    });
    return response;
  };

  const assignJob = async data => {
    const url = `${getApiUrl()}/sxcl/v1/service-providers/${preferredServiceProviderId}/pending-acceptance/${jobId}/assign`;
    const response = await post(url, {
      serviceDate: data?.customerServiceRequest?.requestDate,
      slotTypeCode: data?.customerServiceRequest?.slotTypeCode,
      jobRecordVersion: recordVersion,
    });
    return response;
  };

  const rescheduleJob = async data => {
    const url = `${getApiUrl()}/sxcl/v1/service-jobs/${jobId}/operations/reschedule`;
    const response = await put(url, {
      ...data,
      recordVersion,
    });
    return response;
  };

  const returnToJobPage = () => {
    navigate(`/servicejobs/edit/${serviceJob?.id}`);
  };

  const onDialogClose = () => {
    setOpen(false);
    returnToJobPage(null);
  };

  const buttons = [
    {
      label: isRescheduleFlow
        ? formatMessage({
            id: 'RESCHEDULE',
            defaultMessage: 'Reschedule',
          })
        : formatMessage({
            id: 'DISPATCH',
            defaultMessage: 'Dispatch',
          }),
      onClick: () => formRef.current.submit({ formId: FORM_ID }),
      variant: 'contained',
      color: 'primary',
      startIcon: <IconLibrary.Dispatch />,
    },
    {
      label: formatMessage({
        id: 'RESET',
        defaultMessage: 'Reset',
      }),
      onClick: () => formRef.current.reset(),
      variant: 'outlined',
      color: 'primary',
      startIcon: <IconLibrary.Reset />,
    },
  ];

  const preProcessSubmitData = data => {
    return {
      customerServiceRequests: [data?.customerServiceRequest],
    };
  };

  const onSubmit = async data => {
    const newData = preProcessSubmitData(data);
    let response = null;
    if (rescheduleBySP && preferredServiceProviderId) {
      response = await updateJob(data);
    } else if (preferredServiceProviderId) {
      response = await assignJob(data);
    } else {
      response = await rescheduleJob(newData);
    }
    if (response?.ok) {
      return {
        nextValues: data,
        errors: null,
        callback: () => {
          setSuccess(true);
          setOpen(true);
        },
      };
    }

    return {
      nextValues: data,
      errors: response?.errors,
    };
  };

  const fetchSchema = useCallback(async () => {
    try {
      // eslint-disable-next-line no-console
      if (!rescheduleBySP) {
        const updatedSchema = schema;
        // change Date component title to "Service Provider Appointment Date"
        updatedSchema[0].dateComponentTitle = {
          localeId: 'SERVICE_DATE',
          defaultMessage: 'Service Date',
        };
        return { data: updatedSchema };
      }
      return { data: schema };
    } catch (err) {
      return { data: schema, errors: [{ message: err.message }] };
    }
  }, [rescheduleBySP]);

  return (
    <Box>
      {!success ? (
        <Box sx={{ pt: 1 }}>
          <Form
            dialog
            submitWithReadOnlyFields
            submitWithoutUpdates
            formId={FORM_ID}
            ref={formRef}
            onSubmit={onSubmit}
            fetchSchema={fetchSchema}
            validationSchema={validationSchema(formatMessage)}
            permittedByDefault
            defaultValues={{
              customerServiceRequest: {
                requestDate: null,
                slotTypeCode: slotTypes[0].code,
              },
            }}>
            <Box
              display="flex"
              sx={{
                justifyContent: 'center',
                gap: 2,
                mt: 3,
              }}>
              {buttons.map(btn => {
                const { label, ...rest } = btn;
                return (
                  <Button key={label} {...rest}>
                    {label}
                  </Button>
                );
              })}
            </Box>
          </Form>
        </Box>
      ) : (
        <Dialog
          open={open}
          onClose={() => {
            onDialogClose(null);
          }}
          maxWidth="xs"
          fullWidth>
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <IconLibrary.RescheduleServiceJob />
            <FormattedMessage
              id="JOB_RESCHEDULED"
              defaultMessage="Job Rescheduled"
            />
            <DialogCloseButton
              onClose={() => {
                onDialogClose(null);
              }}
            />
          </DialogTitle>
          <DialogContent>
            <Box sx={{ mt: 2 }} textAlign="center">
              <Typography>
                <FormattedMessage
                  id="JOB_SERVICEJOBNUMBER_WAS_RESCHEDULED_SUCCESSFULLY"
                  defaultMessage="Job {servicejobnumber} was rescheduled successfully."
                  values={{ servicejobnumber: jobNumber }}
                />
              </Typography>
              <Box
                display="flex"
                sx={{
                  justifyContent: 'center',
                  mt: 3,
                }}>
                <Button
                  onClick={() => returnToJobPage()}
                  variant="outlined"
                  startIcon={<IconLibrary.Close />}>
                  <FormattedMessage id="CLOSE" defaultMessage="Close" />
                </Button>
              </Box>
            </Box>
          </DialogContent>
        </Dialog>
      )}
    </Box>
  );
}

export default observer(ServiceDateForm);
